'use client';
import { useId, type ComponentProps } from 'react';
import { useController, useFormContext } from 'react-hook-form';

import { FormFieldMessage } from '../message';

import Select from './select';

import type { FieldValues, Path, PathValue } from 'react-hook-form';

type SelectFieldProps<T> = {
  name: Path<T>;
  disabled?: boolean;
  defaultValue?: PathValue<T, Path<T>>;
  hasFixedHeight?: boolean;
} & Pick<ComponentProps<typeof Select>, 'options' | 'label'> &
  Pick<ComponentProps<typeof FormFieldMessage>, 'hint'>;

const SelectField = <T extends FieldValues>({
  name,
  options,
  label,
  defaultValue,
  disabled = false,
  hint,
  hasFixedHeight,
}: SelectFieldProps<T>) => {
  const id = useId();
  const {
    control,
    formState: { errors },
  } = useFormContext<T>();

  const error = errors[name];

  const { field } = useController({
    control,
    name,
    disabled,
    defaultValue,
  });
  const messageId = `select-message-${id}`;

  return (
    <>
      <Select
        {...field}
        label={label}
        options={options}
        selectedOption={field.value}
        hasFixedHeight={hasFixedHeight}
      />
      <FormFieldMessage
        id={messageId}
        hint={hint}
        error={error?.message as string}
      />
    </>
  );
};

export default SelectField;
